import { useState } from "react";
import { ASPECT_RATIO } from "@/react/components/constants";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { isSpaceMember } from "@/react/helpers/spaceHelpers";
import { useSpaceApi } from "@/react/hooks/useSpaceApi";
import { useAddMemberModal } from "@circle-react/components/Modals/AddSpaceMember";
import {
  useCurrentSpaceContext,
  usePunditUserContext,
} from "@circle-react/contexts";
import type { Space } from "@circle-react/types/Space";
import { CoverImageInput } from "@circle-react-uikit/CoverImageInput";
import { Form } from "@circle-react-uikit/Form";
import { ConditionallyRenderSpaceHeaderTitle } from "./ConditionallyRenderSpaceHeaderTitle";
import { SpaceHeaderNoDropdown } from "./SpaceHeaderNoDropdown";

export interface SpaceHeaderTitleFullScreenProps {
  shouldShowMembersCount?: boolean;
  space: Space;
}

export const SpaceHeaderTitleFullScreen = ({
  space,
  shouldShowMembersCount = false,
}: SpaceHeaderTitleFullScreenProps) => {
  const { id: spaceId } = space;

  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isMember = isSpaceMember(space);
  const [isCoverImageModalOpen, setIsCoverImageModalOpen] = useState(false);
  const { refetch } = useCurrentSpaceContext();
  const { updateSpace } = useSpaceApi();
  const addMemberModal = useAddMemberModal();

  const onAddCoverImage = async (space: Space) => {
    setIsCoverImageModalOpen(false);
    await updateSpace({ ...space, cover_image_visible: true }, spaceId);
    await refetch();
  };

  const onClickAddCoverImage = () => {
    setIsCoverImageModalOpen(true);
  };

  if (isAdmin || isMember) {
    return (
      <>
        <Form onSubmit={onAddCoverImage}>
          {({ triggerFormSubmit }) => (
            <CoverImageInput
              name="cover_image"
              renderer={() => null}
              onImageAttachSuccess={triggerFormSubmit}
              aspectRatio={ASPECT_RATIO.COVER_V3}
              showAddCoverBtn={false}
              isOpen={isCoverImageModalOpen}
              onClose={() => setIsCoverImageModalOpen(false)}
            />
          )}
        </Form>
        <ConditionallyRenderSpaceHeaderTitle
          space={space}
          shouldShowMembersCount={shouldShowMembersCount}
          onClickAddCoverImage={onClickAddCoverImage}
          setIsAddMemberOpen={async () => {
            await addMemberModal.show({ spaceId });
          }}
          isAdmin={isAdmin}
          isMember={isMember}
        />
      </>
    );
  }

  return (
    <SpaceHeaderNoDropdown
      space={space}
      shouldShowMembersCount={shouldShowMembersCount}
    />
  );
};
